import { LOCALE_DEFINITIONS } from "@listto/config/locales";

/**
 * Filter supported locales based on a search term.
 */
export function filterLocales(search: string) {
  const trimmed = search.trim().toLowerCase();
  if (!trimmed) {
    return [...LOCALE_DEFINITIONS];
  }
  return LOCALE_DEFINITIONS.filter(
    (l) =>
      l.label.toLowerCase().includes(trimmed) ||
      l.englishLabel.toLowerCase().includes(trimmed) ||
      l.code.toLowerCase().includes(trimmed),
  );
}

/**
 * Resolves a locale definition from its code.
 */
export function resolveLocaleDefinition(code: string) {
  return LOCALE_DEFINITIONS.find((l) => l.code === code) ?? LOCALE_DEFINITIONS[0];
}

/**
 * CONFIG_OVER_CODE — languages that PLATFORM allows choosing.
 *
 * Client mirror of `i18n.available_locales` (`GET /api/config`). The server is
 * who ENFORCES the policy in the write-path (`locale_allowed` in Rust); this is
 * only the mirror layer, so the UI does not offer something that will be discarded.
 *
 * Rules, deliberately:
 *   - an EMPTY or absent list = no restriction ⇒ all are returned, which is
 *     historical behavior byte for byte (OFF by default);
 *   - a configured code the app has not compiled is ignored: a language
 *     without a catalog is not eligible no matter what the config names;
 *   - if the intersection is empty (bad config), all are returned instead of
 *     leaving the user without a selector: the config cannot break the screen.
 */
export function allowedLocales(available: readonly string[] | null | undefined) {
  if (!available || available.length === 0) {
    return [...LOCALE_DEFINITIONS];
  }
  const set = new Set(available.map((c) => c.trim().toLowerCase()));
  const allowed = LOCALE_DEFINITIONS.filter((l) => set.has(l.code));
  return allowed.length > 0 ? allowed : [...LOCALE_DEFINITIONS];
}

/**
 * `filterLocales` bounded by the platform policy: first the catalog is
 * trimmed to what is allowed and then the search runs within it.
 */
export function filterAllowedLocales(
  search: string,
  available: readonly string[] | null | undefined,
) {
  const pool = allowedLocales(available);
  const trimmed = search.trim().toLowerCase();
  if (!trimmed) {
    return pool;
  }
  return pool.filter(
    (l) =>
      l.label.toLowerCase().includes(trimmed) ||
      l.englishLabel.toLowerCase().includes(trimmed) ||
      l.code.toLowerCase().includes(trimmed),
  );
}
